import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { GuideLinks } from "@/components/GuideLinks";
import { LeadForm } from "@/components/LeadForm";
import { StickyCta } from "@/components/StickyCta";
import { CaseStrip } from "@/components/CasePreview";

const SITE = "https://shopify-freelancer.net";
const PATH = "/shopify-shop-erstellen-lassen";
const TITLE = "Shopify Shop erstellen lassen | Freelancer, Festpreis";
const DESCRIPTION =
  "Shopify Shop erstellen lassen: Struktur, Theme, Produkt- und Kategorieseiten, mobile Umsetzung und begleiteter Start. Festpreis nach Umfang, direkt mit dem Entwickler.";

const faqs = [
  {
    q: "Was kostet ein neuer Shopify-Shop bei Ihnen?",
    a: "Ein kleiner, sauber aufgebauter Shop beginnt bei rund 3.000 €. Der genaue Festpreis hängt von Anzahl und Art der Produkte, Sprachen, Zahlungs- und Versandlogik sowie Schnittstellen ab. Sie bekommen den Preis vor Projektstart schriftlich, nicht als offene Stundenliste.",
  },
  {
    q: "Was kosten Shopify-Shops branchenüblich?",
    a: "Bei Freelancern beginnt ein kleiner Shop bei rund 3.000 €, größere Projekte liegen bei 4.000 bis 10.000 €, komplexe Corporate-Shops ab 12.000 €. Agenturen rufen für Vergleichbares oft 20.000 bis 40.000 € auf, weil Projektleitung und Verwaltung oben draufkommen. Ich arbeite als Freelancer mit Festpreis.",
  },
  {
    q: "Reicht ein Shop allein, um zu verkaufen?",
    a: "Ehrlich gesagt: nein. Der schönste Laden bringt nichts, wenn keiner ihn kennt und keiner reinkommt. Besucher kommen über Google, bezahlte Anzeigen und Empfehlungen. Ich berate Sie dazu offen, auch wenn das bedeutet, erst einmal kleiner zu starten und das Budget in Besucher zu stecken.",
  },
  {
    q: "Wie lange dauert der Aufbau?",
    a: "Das hängt vor allem davon ab, wie vollständig Produktdaten, Bilder und Texte vorliegen. Den Zeitrahmen lege ich erst fest, wenn der Umfang geklärt ist, damit die Zusage auch hält.",
  },
  {
    q: "Brauche ich ein individuell entwickeltes Theme?",
    a: "Meistens nicht. Ein solides Theme, sauber angepasst, ist schneller live und langfristig einfacher zu pflegen. Individuelle Entwicklung lohnt sich bei besonderen Anforderungen wie Konfiguratoren oder B2B-Logiken.",
  },
  {
    q: "Was muss ich selbst liefern?",
    a: "Produktdaten mit Varianten und Preisen, Bilder, Texte, Versand- und Zahlungsbedingungen sowie Ihre Rechtstexte. Je vollständiger dieses Paket ist, desto zügiger und günstiger wird das Projekt.",
  },
  {
    q: "Was passiert nach dem Start?",
    a: "Sie bekommen den Shop übergeben und können ihn selbst pflegen. Wenn danach etwas dazukommt, arbeite ich stundenweise weiter, oder wir schauen uns die Kaufabschlussrate gezielt an.",
  },
];

export const Route = createFileRoute("/shopify-shop-erstellen-lassen")({
  component: ShopErstellenPage,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Shopify Shop erstellen lassen",
          description: DESCRIPTION,
          serviceType: "Shopify Shop Erstellung",
          areaServed: "DACH",
          provider: { "@type": "Person", name: "Nicolai Schnepf", url: SITE },
          offers: {
            "@type": "Offer",
            priceCurrency: "EUR",
            price: "3000",
            description: "Kleiner Shop ab rund 3.000 €, Festpreis nach Umfang, schriftlich vor Projektstart.",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});

const scope = [
  {
    title: "Struktur und Planung",
    text: "Sortiment, Kategorien, Varianten und Navigation werden vor dem ersten Klick im Adminbereich geordnet. Das entscheidet später darüber, ob Kunden finden, was sie suchen.",
  },
  {
    title: "Theme und Gestaltung",
    text: "Ein passendes Theme, an Ihre Marke angepasst: Farben, Schriften, Startseite, Vertrauenselemente. Sauber im Code, ohne App-Stapel für Dinge, die das Theme selbst kann.",
  },
  {
    title: "Produkt- und Kategorieseiten",
    text: "Bilder, Varianten, Beschreibungen, Versandhinweise und Verfügbarkeiten so aufgebaut, dass eine Kaufentscheidung möglich ist, nicht nur eine Produktanzeige.",
  },
  {
    title: "Mobile zuerst",
    text: "Der größte Teil Ihrer Besucher kommt vom Handy. Produktseite, Warenkorb und Kaufabschluss werden auf dem Gerät geprüft, auf dem sie tatsächlich benutzt werden.",
  },
  {
    title: "Start und Übergabe",
    text: "Zahlungen, Versandregeln, Steuern, Rechtstexte und Weiterleitungen werden vor dem Start durchgegangen. Danach zeige ich Ihnen, wie Sie den Shop selbst pflegen.",
  },
];

const steps = [
  { no: "01", title: "Gespräch und Umfang", text: "Sie schildern Vorhaben und Sortiment. Ich sage Ihnen, was sinnvoll ist und was Sie sich sparen können." },
  { no: "02", title: "Festpreis und Plan", text: "Umfang und Preis stehen schriftlich fest, bevor gebaut wird. Keine offene Stundenliste." },
  { no: "03", title: "Aufbau", text: "Struktur, Theme, Seiten, Zahlungen und Versand. Zwischenstände sehen Sie im Testshop." },
  { no: "04", title: "Start und Übergabe", text: "Kontrolle vor dem Livegang, dann Übergabe mit kurzer Einweisung." },
];

function ShopErstellenPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />

      <main id="top">
        <section className="veil border-b border-border/60 py-16 md:py-24">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 md:grid-cols-2 md:items-center">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
                Shopify · Shop-Erstellung
              </p>
              <h1 className="mt-5 text-4xl font-bold leading-[1.05] md:text-6xl">
                Shopify-Shop <span className="text-signal">erstellen lassen</span>, ohne
                Umweg über eine Agentur.
              </h1>
              <p className="mt-6 max-w-xl text-lg text-muted-foreground">
                Ich baue Ihren Shop von der Struktur bis zum Start: Sortiment ordnen, Theme
                anpassen, Produkt- und Kategorieseiten aufbauen, mobil prüfen, live gehen.
                Sie sprechen dabei durchgehend mit der Person, die den Shop auch baut.
              </p>
              <p className="mt-4 text-sm text-muted-foreground">
                Kleiner Shop ab rund 3.000 €, Festpreis schriftlich vor Start. Spätere
                Änderungen und Erweiterungen: 110 € netto pro Stunde.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#anfrage"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                >
                  Kostenlose Einschätzung erhalten <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link
                  to="/cases"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold transition-colors hover:border-primary"
                >
                  Kundenprojekte ansehen
                </Link>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Unverbindlich · Antwort innerhalb von 48 Stunden
              </p>
            </div>
            <div id="anfrage">
              <LeadForm
                source="lp-shop-erstellung"
                showServices
                defaultService="Shop-Erstellung"
                headline="Neuen Shop planen"
                sub="Sie erhalten innerhalb von 48 Stunden eine kurze Einschätzung zu Ihrem Vorhaben, den empfohlenen nächsten Schritt und, soweit anhand Ihrer Angaben möglich, einen groben Kostenrahmen."
                messagePlaceholder="Was verkaufen Sie, wie viele Produkte, gibt es schon Bilder und Texte?"
              />
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Was zum Aufbau gehört</h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {scope.map((s) => (
                <div key={s.title} className="shadow-lift rounded-lg border border-border bg-card p-6">
                  <h3 className="text-base font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Was ein Shopify-Shop kostet</h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              Klare Zahlen statt Nebel. Das hier sind die Pakete, die ich anbiete.
            </p>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              <div className="shadow-lift rounded-lg border border-border bg-card p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Kleiner Shop</p>
                <p className="text-signal mt-2 text-3xl font-bold">ab 3.000 €</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Ein solides Theme, passend zu Ihrer Marke, überschaubares Sortiment, Zahlungs- und
                  Versandarten eingerichtet. Gut, um schlank zu starten und mit echten Verkäufen zu
                  lernen.
                </p>
              </div>
              <div className="shadow-lift rounded-lg border border-border bg-card p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Wachsender Shop</p>
                <p className="text-signal mt-2 text-3xl font-bold">4.000 bis 10.000 €</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Mehr Produkte, weitere Sprachen, Kundenkonten für Geschäftskunden oder
                  Anbindungen wie DATEV und das Warenwirtschaftssystem. Darin bewegen sich Freelancer
                  branchenüblich.
                </p>
              </div>
              <div className="shadow-lift rounded-lg border-2 border-primary/60 bg-card p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-primary">Corporate-Shop</p>
                <p className="text-signal mt-2 text-3xl font-bold">ab 12.000 €</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Komplexe Sortimente, eigene Theme-Entwicklung, mehrere Sprachen und Märkte,
                  tiefe Anbindungen an ERP und Warenwirtschaft, B2B-Logiken und Freigabeprozesse.
                  Alles auf den einzelnen Fall zugeschnitten.
                </p>
              </div>
            </div>

            <div className="mt-6 rounded-lg border border-dashed border-border/70 bg-card/40 p-6">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                Nur zum Vergleich, nicht buchbar: Agentur
              </p>
              <p className="mt-2 text-lg text-muted-foreground">
                Für vergleichbare Vorhaben rufen Agenturen{" "}
                <span className="font-semibold text-foreground">oft 20.000 bis 40.000 €</span> auf:
                Projektleitung, Designabteilung und Verwaltung kommen oben drauf. Bei mir sprechen
                Sie mit der Person, die den Shop auch baut, und zahlen nur die eigentliche Arbeit.
              </p>
            </div>

            <p className="mt-6 text-sm text-muted-foreground">
              Ihr Festpreis steht schriftlich fest, bevor es losgeht. Keine offene Stundenliste.
            </p>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-3xl px-5">
            <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
              Ehrliche Beratung
            </p>
            <h2 className="mt-4 text-3xl font-bold md:text-4xl">
              Der schönste Laden bringt nichts, <span className="text-signal">wenn keiner reinkommt.</span>
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Stellen Sie sich ein Geschäft in der Fußgängerzone vor: liebevoll eingerichtet, gute
              Produkte, faire Preise. Aber es liegt in einer Seitenstraße, und niemand weiß, dass es
              existiert. Genau so ist es mit einem Onlineshop. Der Shop allein verkauft nichts, das
              ist Wunschdenken. Ihr Shop muss verkaufen, und dafür braucht er Besucher.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Als seriöser Berater sage ich Ihnen das vorher, nicht nachher. Besucher kommen über
              Google, über bezahlte Anzeigen und über Empfehlungen. Deshalb baue ich Ihren Shop von
              Anfang an conversion-optimiert: damit aus Besuchern Käufer werden. Und wenn Sie wollen,
              richte ich die Besucherquellen gleich mit ein, etwa Google Ads und Microsoft Ads sowie
              die Sichtbarkeit in KI-Assistenten wie ChatGPT, damit Ihr Laden nicht in der
              Seitenstraße stehen bleibt.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Manchmal lautet meine ehrliche Empfehlung: Starten Sie mit dem kleineren Shop und
              stecken Sie den Rest des Budgets in Besucher. Das verkauft mehr als der perfekte Shop,
              den keiner kennt.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/shopify-conversion-optimierung"
                className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold transition-colors hover:border-primary"
              >
                Conversion-Optimierung ansehen
              </Link>
              <a
                href="#anfrage"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
              >
                Vorhaben besprechen <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </section>

        <CaseStrip
          slugs={["snydergolf", "kremka"]}
          title="Aufgebaut und übernommen"
          intro="Snyder Golf ist von WooCommerce auf Shopify migriert, inklusive Produkten, Kunden und Weiterleitungen. Bei kremka ging es um Struktur, Suche und Nachbestellung im laufenden B2B-Shop."
        />

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">So läuft es ab</h2>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {steps.map((s) => (
                <div key={s.no} className="rounded-lg border border-border bg-card p-6">
                  <span className="text-signal font-display text-3xl font-bold">{s.no}</span>
                  <h3 className="mt-3 text-base font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>


        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Häufige Fragen</h2>
            <div className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border">
              {faqs.map((f) => (
                <div key={f.q} className="bg-card p-6">
                  <h3 className="text-base font-semibold">{f.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Ausführlicher Hintergrund zu Kosten und Auswahl:{" "}
              <Link
                to="/ratgeber/shopify-shop-erstellen-lassen"
                className="text-primary underline underline-offset-4"
              >
                Ratgeber Shopify Shop erstellen lassen
              </Link>
              . Bestehendes Theme anpassen statt neu bauen?{" "}
              <Link to="/shopify-theme-anpassung" className="text-primary underline underline-offset-4">
                Theme-Anpassung
              </Link>
              .
            </p>
          </div>
        </section>

        <section id="kontakt" className="py-20 pb-28 lg:pb-20">
          <div className="mx-auto grid max-w-5xl gap-10 px-5 md:grid-cols-2 md:items-center">
            <div>
              <h2 className="text-3xl font-bold md:text-4xl">
                Erzählen Sie mir kurz von <span className="text-signal">Ihrem Vorhaben</span>.
              </h2>
              <p className="mt-5 text-muted-foreground">
                Sie bekommen innerhalb von 48 Stunden eine kurze Einschätzung, den empfohlenen
                nächsten Schritt und, soweit Ihre Angaben es hergeben, einen groben
                Kostenrahmen. Kostenlos und unverbindlich.
              </p>
            </div>
            <LeadForm
              source="lp-shop-erstellung"
              compact={false}
              showServices
              defaultService="Shop-Erstellung"
              headline="Shop-Erstellung einschätzen lassen"
              cta="Kostenlose Einschätzung erhalten"
              messagePlaceholder="Was verkaufen Sie, wie viele Produkte, gibt es schon Bilder und Texte?"
            />
          </div>
        </section>
      </main>

      <StickyCta targetId="anfrage" />
      <GuideLinks
        links={[
        { to: "/ratgeber/shopify-kosten", label: "Was kostet ein Shopify Shop", text: "Aufbau, Betrieb und laufende Kosten im Ueberblick." },
        { to: "/blog/shopify-rechtssicher-deutschland", label: "Rechtssicher einrichten", text: "Pflichtangaben fuer deutsche Shops." },
        { to: "/blog/shopify-zahlungsarten-deutschland", label: "Zahlungsarten in Deutschland", text: "Welche Zahlarten deutsche Kaeufer erwarten." },
        ]}
      />
      <SiteFooter />
    </div>
  );
}
